import React, { useState } from 'react';

import {
  Box,
  Stack,
  Center,
  useDisclosure,
  Text,
  HStack,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDeepCompareEffect } from 'react-use';

import {
  Card,
  PageHeaderWithIcon,
  renderCloudIcon,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { LearnMore } from 'components/DataDisplay/KnowledgeBase/Components/LearnMore';
import { useKnowledgeBase } from 'components/DataDisplay/KnowledgeBase/hook/useKnowledgeBase';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import {
  ActionButton,
  AddButton,
  DeleteActionButton,
} from 'components/DataEntry';
import { LearnMoreButton } from 'components/DataEntry/Button/LearnMoreButton';
import { AttachIcon, EditIcon, RemediateIcon } from 'components/Icons';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { renderCustomTag } from 'containers/PolicyHub/Blueprints/Components/utils';
import { useRemediation } from 'containers/PolicyHub/Remediation/Components/hooks/useRemediation';
import { AlertsAssociatedToRemediation } from 'containers/PolicyHub/Remediation/Components/RemediationTable/AlertsAssociatedToRemediation';
import { AttachRemediationToAlert } from 'containers/PolicyHub/Remediation/Components/RemediationTable/AttachRemediationToAlert';
import { RemediationFilters } from 'containers/PolicyHub/Remediation/Components/RemediationTable/RemediationFilters';
import { selectRemediation } from 'containers/PolicyHub/Remediation/selector';

import { actions } from '../../slice';

export const RemediationTable: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { remediationAction, remediationList } = useSelector(selectRemediation);
  const {
    getRemediationList,
    getRemediationCategories,
    resourceType,
    remediationType,
    categoryOptions,
    category,
    updateValue,
    cloud,
  } = useRemediation();
  const attachRemediation = useDisclosure();
  const associatedAlerts = useDisclosure();
  const [currentRecord, setCurrentRecord] = useState({});
  const { cloudNativeMapping } = useResourceType();
  const { setLearnMore } = useKnowledgeBase();

  useDeepCompareEffect(() => {
    getRemediationList();
  }, [getRemediationList]);

  const handleDelete = row => {
    dispatch(
      actions.deleteRemediation({
        q: { id: row?.id },
        onSuccess: () => getRemediationCategories(),
      }),
    );
  };

  const renderAction = ({ row }) => {
    return (
      <Center
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <ActionButton
          label="Attach alert to remediation"
          icon={<AttachIcon />}
          onClick={() => {
            attachRemediation.onOpen();
            setCurrentRecord(row);
          }}
        />
        <ActionButton
          label="Edit"
          icon={<EditIcon />}
          onClick={() => {
            navigate(`/policy-hub/remediation/edit/${row?.id}`);
          }}
          isDisabled={row?.created_by === 'Ariksa'}
        />
        <DeleteActionButton
          onConfirm={() => handleDelete(row)}
          name={row?.name}
          type={'remediation'}
          isLoading={remediationAction.isLoading}
          isDisabled={row?.created_by === 'Ariksa'}
        />
      </Center>
    );
  };

  const columns = [
    {
      header: <Box pl={12}>Description</Box>,
      accessor: 'name',
      align: 'left',
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={row?.resource_type}
          //bgColor="primary"
          tooltip={row?.name}
        >
          <Text whiteSpace="wrap" noOfLines={2}>
            {row?.description}
          </Text>
        </WithResourceIcon>
      ),
    },
    {
      header: 'Cloud',
      render: ({ row }) => (
        <Center>{renderCloudIcon({ provider: row?.cloud })}</Center>
      ),
      styles: { width: '100px', cell: { w: '100px' } },
    },
    {
      header: 'Resource Type',
      align: 'left',
      accessor: 'resource_type',
      render: ({ value }) => {
        const resource = cloudNativeMapping?.[value];
        return (
          <StackedCell
            upper={resource?.resource_alias ?? resource?.cloud_native_name}
            lower={resource?.cloud_agnostic_name}
          />
        );
      },
      styles: { width: '200px', cell: { w: '200px' } },
    },
    {
      header: 'Attached To',
      align: 'left',
      accessor: 'policy_count',
      render: ({ value }) =>
        formatNumber(value) + ' issue' + (value === 1 ? '' : 's'),
      styles: { width: '100px', cell: { w: '100px' } },
    },
    {
      header: 'Created By',
      align: 'left',
      accessor: 'created_by',
      styles: { width: '120px', cell: { w: '120px' } },
    },
    {
      header: 'Context',
      align: 'center',
      accessor: 'created_by',
      render: ({ value }) => {
        return renderCustomTag(value);
      },
      styles: { width: '100px', cell: { maxWidth: '100px' } },
    },
    {
      header: 'Actions',
      accessor: 'actions',
      render: renderAction,
      styles: { width: '100px', cell: { maxWidth: '100px' } },
    },
  ];

  return (
    <Card styles={{ card: { w: 'full', h: 'full', flex: 1 } }}>
      <Stack h="full" spacing={4} w="full">
        <HStack w="full" justify="space-between">
          <Box>
            <PageHeaderWithIcon
              label="Remediation"
              icon={<RemediateIcon />}
              reversed
            />
          </Box>
          <HStack>
            <LearnMoreButton
              onOpen={() => setLearnMore('remediation')}
              label="Remediation"
            />
            <AddButton
              onClick={() => {
                navigate('/policy-hub/remediation/add');
              }}
              label="Create Remediation"
            />
          </HStack>
        </HStack>

        <RemediationFilters
          resourceType={resourceType}
          cloud={cloud}
          remediationType={remediationType}
          updateValue={updateValue}
          category={category}
          categoryOptions={categoryOptions}
        />
        <Box flex={1}>
          <Table
            data={remediationList.data}
            columns={columns}
            isLoading={remediationList.isLoading}
            cursor="pointer"
            styles={{ header: { zIndex: 800 } }}
            onRowClick={row => {
              setCurrentRecord(row);
              associatedAlerts.onOpen();
            }}
            pagination={{
              totalCount: remediationList.page.totalCount,
              pageInfo: remediationList.page.info,
              onChange: pageInfo => {
                dispatch(actions.updateRemediationListPagination(pageInfo));
              },
            }}
          />
        </Box>
        {attachRemediation.isOpen && (
          <AttachRemediationToAlert
            isOpen={attachRemediation.isOpen}
            onClose={attachRemediation.onClose}
            row={currentRecord}
            actionType="Attach"
          />
        )}
        {associatedAlerts.isOpen && (
          <AlertsAssociatedToRemediation
            row={currentRecord}
            isOpen={associatedAlerts.isOpen}
            onClose={associatedAlerts.onClose}
          />
        )}
      </Stack>
      <LearnMore />
    </Card>
  );
};
